import React, { useState, useEffect } from 'react';
import {
  Alert,
  Accordion, AccordionSummary, AccordionDetails,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Textarea,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import {
  ShieldCheck,
  Warning,
  ArrowClockwise,
  Buildings,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { MESSAGING_PURPOSES, type MessagingPurpose, type MessagingReadiness } from '../../types/communications';

interface Registration {
  status: string;
  status_display: string;
  is_approved: boolean;
  is_in_review: boolean;
  legal_business_name: string;
  business_type: string;
  ein: string;
  website: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  legal_entity_type: string;
  contact_job_position: string;
  contact_title: string;
  stock_exchange: string;
  stock_ticker: string;
  privacy_policy_url: string;
  terms_url: string;
  opt_in_url: string;
  opt_in_description: string;
  sync_error: string;
  last_checked_at: string | null;
  last_attempted_at: string | null;
  can_edit: boolean;
  identity_locked: boolean;
  campaign_retry_supported: boolean;
  campaign_sid?: string;
  numbers_total: number;
  numbers_ready: number;
  messaging_purposes: MessagingPurpose[];
  campaign_description: string;
  message_samples: { purpose: MessagingPurpose; text: string }[];
  expected_monthly_segments: number;
  failure_reason: string;
  missing_fields: string[];
  submission_count: number;
  can_submit?: boolean;
  blocked_reason?: string | null;
  pricing?: { total: string; monthly_fee: string } | null;
}

interface FilingReview {
  review_token: string; attestation: string;
  filing: { details: Record<string, unknown>; campaign: Record<string, unknown> };
  public_evidence: Record<string, unknown>;
}

const BUSINESS_TYPES = [
  { value: 'PRIVATE_PROFIT', label: 'Private company (LLC, S-Corp, C-Corp)' },
  { value: 'SOLE_PROPRIETOR', label: 'Sole proprietor' },
  { value: 'PUBLIC_PROFIT', label: 'Publicly traded company' },
  { value: 'NON_PROFIT', label: 'Non-profit' },
];

const statusColor = (status: string): 'success' | 'warning' | 'danger' | 'neutral' => {
  if (status === 'approved') return 'success';
  if (status.endsWith('_failed') || ['needs_attention', 'suspended', 'cancelled'].includes(status)) return 'danger';
  if (status.endsWith('_pending')) return 'warning';
  return 'neutral';
};

/**
 * Registering the contractor's business with the phone carriers.
 *
 * The details here go straight into a legal filing that costs money and can be
 * rejected on a typo, so the form says what each field is for and the fee is
 * stated — as non-refundable — before the button that spends it.
 */
const RegistrationForm: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Partial<Registration>>({});
  const [error, setError] = useState<string | null>(null);
  const [acceptedFees, setAcceptedFees] = useState(false);
  const [step, setStep] = useState(0);
  const [review, setReview] = useState<FilingReview | null>(null);
  const [confirmedFiling, setConfirmedFiling] = useState(false);
  const edit = (changes: Partial<Registration>) => {
    setForm(prev => ({ ...prev, ...changes }));
    setReview(null); setAcceptedFees(false); setConfirmedFiling(false);
  };
  const message = (err: any, fallback: string) => {
    const data = err?.responseData || err?.response?.data;
    if (data && typeof data === 'object') return Object.entries(data)
      .map(([key, val]) => `${key === 'error' || key === 'detail' ? '' : key.replaceAll('_', ' ') + ': '}${Array.isArray(val) ? val.join(' ') : String(val)}`).join(' ');
    return fallback;
  };

  const { data: registration, isLoading, isError, error: loadError, refetch } = useQuery<Registration>({
    queryKey: ['a2p-registration', organizationId],
    queryFn: async () =>
      (await api.get<Registration>('/api/inbox/a2p-registration/')).data,
    refetchInterval: (query) => query.state.data?.is_in_review ? 15000 : false,
    retry: 1,
  });

  useEffect(() => { if (registration?.is_approved) setStep(-1); }, [registration?.is_approved]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['a2p-registration', organizationId] });

  const saveDetails = useMutation({
    mutationFn: (payload: Partial<Registration>) =>
      api.patch('/api/inbox/a2p-registration/details/', payload),
    onSuccess: () => { setError(null); setForm({}); setReview(null); setAcceptedFees(false); invalidate(); },
    onError: (err: any) =>
      setError(message(err, 'Could not save those details.')),
  });

  const submit = useMutation({
    mutationFn: () => api.post('/api/inbox/a2p-registration/submit/', {
      details: form, accept_fees: acceptedFees,
      expected_total: registration?.pricing?.total,
      expected_monthly: registration?.pricing?.monthly_fee,
      review_token: review?.review_token, confirm_filing: confirmedFiling,
    }),
    onSuccess: () => { setError(null); setForm({}); setReview(null); setAcceptedFees(false); invalidate(); },
    onError: (err: any) => {
      setAcceptedFees(false);
      setError(message(err, 'Could not submit the registration.'));
      invalidate();
    },
  });

  const reviewFiling = useMutation({
    mutationFn: () => api.post<FilingReview>('/api/inbox/a2p-registration/review/', { details: form }),
    onSuccess: response => { setReview(response.data); setStep(2); setError(null); setAcceptedFees(false); setConfirmedFiling(false); },
    onError: err => setError(message(err, 'Could not validate the filing and public consent pages.')),
  });
  const { data: filings } = useQuery({
    queryKey: ['a2p-filings', organizationId, registration?.submission_count],
    queryFn: async () => (await api.get<{ results: { version: number; created_at: string; fees: Record<string, string> }[] }>('/api/inbox/a2p-registration/filings/')).data.results,
  });

  const refresh = useMutation({
    mutationFn: () => api.post('/api/inbox/a2p-registration/refresh/', {}),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (err: any) => setError(message(err, 'Could not request a status check.')),
  });

  if (isError) return <Alert color="warning" sx={{ mb: 3 }} endDecorator={<Button variant="plain" onClick={() => refetch()}>Retry</Button>}>
    {message(loadError, 'Could not load business texting registration. Organization admins can manage registration here.')}
  </Alert>;

  if (isLoading || !registration) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const value = (field: keyof Registration) =>
    (form[field] as string) ?? (registration[field] as string) ?? '';

  const set = (field: keyof Registration) => (next: string) =>
    edit({ [field]: next });

  const busy = saveDetails.isPending || submit.isPending || reviewFiling.isPending;
  const locked = !registration.can_edit || busy;
  const identityLocked = locked || registration.identity_locked;
  const consentFields = ['privacy_policy_url', 'terms_url', 'opt_in_url', 'opt_in_description'];
  const selectField = (name: keyof Registration, label: string, choices: string[]) => (
    <Grid xs={12} sm={6}><FormControl><FormLabel>{label}</FormLabel>
      <Select value={value(name) || null} disabled={identityLocked} placeholder="Choose one"
        onChange={(_, next) => set(name)(next || '')}>
        {choices.map(option => <Option key={option} value={option}>{option}</Option>)}
      </Select>
    </FormControl></Grid>
  );

  const field = (
    name: keyof Registration,
    label: string,
    helper?: string,
    span: number = 6,
  ) => (
    <Grid xs={12} sm={span}>
      <FormControl>
        <FormLabel>{label}</FormLabel>
        <Input
          value={value(name)}
          disabled={consentFields.includes(name) ? locked : identityLocked}
          onChange={(e) => set(name)(e.target.value)}
        />
        {helper && <FormHelperText>{helper}</FormHelperText>}
      </FormControl>
    </Grid>
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1}
          sx={{ mb: 1 }}
        >
          <Typography level="title-lg" startDecorator={<Buildings size={20} />}>
            Business Texting Registration
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              variant="soft"
              color={statusColor(registration.status)}
              startDecorator={
                registration.is_approved ? <ShieldCheck size={14} /> : undefined
              }
            >
              {registration.status_display}
            </Chip>
            {registration.status !== 'not_started' && registration.status !== 'collecting' && (
              <Button
                size="sm"
                variant="plain"
                startDecorator={<ArrowClockwise size={16} />}
                loading={refresh.isPending}
                disabled={busy}
                onClick={() => refresh.mutate()}
              >
                Check
              </Button>
            )}
          </Stack>
        </Stack>

        <Typography level="body-sm" sx={{ mb: 2 }}>
          US phone carriers require every business that texts customers to be
          registered under its own name. ApexOS enables business texting after campaign approval and number activation.
          This setup supports US businesses with an EIN and up to 10,000 monthly message segments.
          Do not enter a Social Security number. International, toll-free, marketing-only and higher-volume campaigns require separate support review.
        </Typography>

        {registration.is_approved && (
          <Alert color="success" variant="soft" sx={{ mb: 2 }}>
            Your business registration is approved. {registration.numbers_ready} of {registration.numbers_total} active texting numbers have confirmed carrier registration.
            {registration.numbers_ready < registration.numbers_total && ' Other numbers are still pending or need review. See each number’s activation status below.'}
          </Alert>
        )}

        {registration.is_in_review && (
          <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
            The carriers are reviewing your registration. This usually takes a
            few business days. We check automatically every 15 minutes and notify your organization admins when the status changes. You can leave this page.
          </Alert>
        )}

        {registration.failure_reason && (
          <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
            <Box>
              <Typography level="title-sm">Action needed</Typography>
              <Typography level="body-sm">{registration.failure_reason}</Typography>
              <Typography level="body-xs" sx={{ mt: 0.5 }}>
                {registration.can_edit ? 'Correct the details below and review the displayed fees before resubmitting.' : 'Contact ApexOS support for help with this registration.'}
              </Typography>
            </Box>
          </Alert>
        )}

        {registration.sync_error && <Alert color="warning" sx={{ mb: 2 }}>{registration.sync_error}</Alert>}
        <Typography level="body-xs">
          {registration.last_checked_at ? `Last successful carrier check: ${new Date(registration.last_checked_at).toLocaleString()}` : 'No completed carrier status check yet.'}
          {' '}Approved registrations are rechecked daily.
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mb: 2 }}>
          {['1. Business details', '2. Purposes and consent', '3. Review filing and fees'].map((label, index) =>
            <Button key={label} variant={step === index ? 'solid' : 'outlined'} size="sm" onClick={() => setStep(index)}>{label}</Button>)}
        </Stack>
        <Grid container spacing={2} sx={{ display: step === 0 ? 'flex' : 'none' }}>

          {field(
            'legal_business_name',
            'Legal business name',
            'Exactly as registered with the IRS — carriers match on it',
          )}
          <Grid xs={12} sm={6}>
            <FormControl>
              <FormLabel>Business type</FormLabel>
              <Select
                value={value('business_type') || null}
                disabled={identityLocked}
                onChange={(_, next) => set('business_type')((next as string) || '')}
              >
                {BUSINESS_TYPES.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {selectField('legal_entity_type', 'Legal structure', ['Sole Proprietorship', 'Partnership', 'Corporation', 'Limited Liability Corporation', 'Co-operative', 'Non-profit Corporation'])}
          {value('business_type') === 'PUBLIC_PROFIT' && <>
            {selectField('stock_exchange', 'Stock exchange', ['NASDAQ', 'NYSE', 'AMEX', 'TSX', 'LON', 'ASX'])}
            {field('stock_ticker', 'Stock ticker')}
          </>}
          {field('ein', 'Federal Tax ID (EIN)', 'Nine digits, as on your IRS letter')}
          {field('website', 'Business website', 'Reviewers will visit this')}
          {field('street', 'Street address', undefined, 12)}
          {field('city', 'City', undefined, 4)}
          {field('state', 'State', undefined, 4)}
          {field('postal_code', 'ZIP code', undefined, 4)}
          {field('contact_first_name', 'Contact first name')}
          {field('contact_last_name', 'Contact last name')}
          {field('contact_email', 'Contact email')}
          {field('contact_phone', 'Contact phone', 'Include the country code, for example +1 312 555 0123')}
          {selectField('contact_job_position', 'Contact role', ['CEO', 'CFO', 'Director', 'GM', 'VP', 'General Counsel', 'Other'])}
          {field('contact_title', 'Job title', 'For example, Owner or Operations Director')}
        </Grid>
        <Grid container spacing={2} sx={{ display: step === 1 ? 'flex' : 'none' }}>
          <Grid xs={12}><Typography level="title-md">Message purposes</Typography>
            <Typography level="body-sm">Choose only the messages you intend to send. Each recipient must separately agree to the relevant purpose.</Typography>
            <Stack spacing={1} sx={{ my: 2 }}>
              {Object.entries(MESSAGING_PURPOSES).map(([purpose, label]) => <Checkbox key={purpose} label={label} disabled={locked}
                checked={(form.messaging_purposes ?? registration.messaging_purposes).includes(purpose as MessagingPurpose)}
                onChange={event => edit({ messaging_purposes: event.target.checked
                  ? [...(form.messaging_purposes ?? registration.messaging_purposes), purpose as MessagingPurpose]
                  : (form.messaging_purposes ?? registration.messaging_purposes).filter(p => p !== purpose) })} />)}
            </Stack>
          </Grid>
          <Grid xs={12}><FormControl><FormLabel>Describe this campaign</FormLabel>
            <Textarea minRows={3} value={value('campaign_description')} disabled={locked}
              onChange={event => set('campaign_description')(event.target.value)} />
            <FormHelperText>40–3,000 characters. Explain the audiences, actual messages and why they receive them.</FormHelperText>
          </FormControl></Grid>
          <Grid xs={12} sm={6}><FormControl><FormLabel>Expected monthly message segments</FormLabel>
            <Input type="number" value={form.expected_monthly_segments ?? registration.expected_monthly_segments}
              slotProps={{ input: { min: 1, max: 10000 } }} disabled={locked}
              onChange={event => edit({ expected_monthly_segments: Number(event.target.value) })} />
            <FormHelperText>Long messages and emoji can use several segments.</FormHelperText>
          </FormControl></Grid>
          <Grid xs={12}><Typography level="title-md">Actual sample messages</Typography>
            <Typography level="body-sm">Provide 2–5 samples covering every selected purpose. Include your legal business name and STOP instructions.</Typography>
            {(form.message_samples ?? registration.message_samples).map((sample, index) => <Stack spacing={1} key={index} sx={{ mt: 2 }}>
              <Select aria-label={`Sample ${index + 1} purpose`} value={sample.purpose} disabled={locked}
                onChange={(_, purpose) => edit({ message_samples: (form.message_samples ?? registration.message_samples).map((row, i) => i === index ? { ...row, purpose: purpose as MessagingPurpose } : row) })}>
                {(form.messaging_purposes ?? registration.messaging_purposes).map(p => <Option key={p} value={p}>{MESSAGING_PURPOSES[p]}</Option>)}
              </Select>
              <Textarea aria-label={`Sample message ${index + 1}`} value={sample.text} minRows={2} disabled={locked}
                onChange={event => edit({ message_samples: (form.message_samples ?? registration.message_samples).map((row, i) => i === index ? { ...row, text: event.target.value } : row) })} />
              {!locked && <Button size="sm" variant="plain" color="neutral" onClick={() => edit({ message_samples: (form.message_samples ?? registration.message_samples).filter((_, i) => i !== index) })}>Remove sample</Button>}
            </Stack>)}
            {!locked && (form.message_samples ?? registration.message_samples).length < 5 && <Button sx={{ mt: 1 }} variant="outlined"
              onClick={() => edit({ message_samples: [...(form.message_samples ?? registration.message_samples), { purpose: (form.messaging_purposes ?? registration.messaging_purposes)[0] || 'customer_updates', text: '' }] })}>Add sample</Button>}
          </Grid>
          <Grid xs={12}><Divider sx={{ my: 1 }} /><Typography level="title-md">How recipients agree to texts</Typography>
            <Typography level="body-sm">Describe the real consent process for each selected audience. Public evidence must identify your business and explain optional consent, privacy, STOP and HELP.</Typography></Grid>
          {field('privacy_policy_url', 'Business privacy policy URL')}
          {field('terms_url', 'Business texting terms URL')}
          {field('opt_in_url', 'Consent form or evidence URL', 'Link to the live form, or a publicly viewable image of your paper form or phone consent script.', 12)}
          <Grid xs={12}><FormControl><FormLabel>Describe your actual opt-in process</FormLabel>
            <Textarea minRows={4} value={value('opt_in_description')} disabled={locked}
              onChange={event => set('opt_in_description')(event.target.value)}
              placeholder="Explain where customers sign up, the exact consent wording they see or hear, and how you record their agreement." />
            <FormHelperText>100–1,800 characters. Include the business name, what texts cover, message frequency, message/data rates, STOP and HELP instructions, and whether consent is optional. Do not describe a checkbox or process you do not actually use.</FormHelperText>
          </FormControl></Grid>
        </Grid>

        {step === 2 && <Box sx={{ mt: 2 }}>
          {!review ? <Alert color="neutral">Validate the details in steps 1 and 2 to generate the exact filing for review.</Alert> : <Stack spacing={2}>
            <Typography level="title-md">Review the filing you are authorizing</Typography>
            <Typography level="body-sm">Public evidence URLs passed an accessibility and security check. Confirm the pages accurately describe your business and actual consent process. This review expires in 15 minutes.</Typography>
            {Object.entries(review.filing.details).filter(([key]) => !['message_samples', 'messaging_purposes'].includes(key)).map(([key, val]) =>
              <Box key={key}><Typography level="body-xs" fontWeight="lg">{key.replaceAll('_', ' ')}</Typography><Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{String(val || '—')}</Typography></Box>)}
            <Typography level="title-sm">Campaign submitted to carriers</Typography>
            {Object.entries(review.filing.campaign).map(([key, val]) => <Box key={key}>
              <Typography level="body-xs" fontWeight="lg">{key.replace(/([a-z])([A-Z])/g, '$1 $2')}</Typography>
              <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{Array.isArray(val) ? val.join('\n\n') : String(val)}</Typography>
            </Box>)}
            <Checkbox checked={confirmedFiling} disabled={busy} onChange={event => setConfirmedFiling(event.target.checked)} label={review.attestation} />
          </Stack>}
        </Box>}

        {error && (
          <Alert color="danger" variant="soft" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {registration.can_edit && (
          <>
            {registration.pricing && (
              <Alert color="neutral" variant="soft" sx={{ mt: 2 }}>
                <Box>
                  <Typography level="title-sm" startDecorator={<Warning size={16} />}>
                    One-time review fees: ${Number(registration.pricing.total).toFixed(2)} · Monthly carrier fee: ${Number(registration.pricing.monthly_fee).toFixed(2)}
                  </Typography>
                  <Typography level="body-sm">
                    Fees are taken from your usage balance; add funds in Settings → Billing.
                    Completed carrier reviews are not refundable. Fees for reviews that never start are returned after a confirmed failure.
                    Updating an existing rejected campaign does not incur a second review fee.
                    The monthly fee is billed on the first of each month for approved or suspended campaigns until deactivation.
                    Carrier approval and delivery timing are controlled by Twilio and the carriers.
                  </Typography>
                </Box>
              </Alert>
            )}

            {registration.missing_fields.length > 0 && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 1.5 }}>
                Still needed: {registration.missing_fields.join(', ')}
              </Typography>
            )}

            <Checkbox sx={{ mt: 2 }} checked={acceptedFees} disabled={busy || !registration.pricing || !review || step !== 2}
              onChange={event => setAcceptedFees(event.target.checked)}
              label="I confirm these business and consent details are accurate and authorize the displayed one-time and monthly fees." />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                loading={saveDetails.isPending}
                disabled={submit.isPending}
                onClick={() => saveDetails.mutate(form)}
              >
                Save for later
              </Button>
              {step === 0 && <Button onClick={() => setStep(1)}>Continue to purposes and consent</Button>}
              {step !== 0 && <Button variant="outlined" loading={reviewFiling.isPending} disabled={busy} onClick={() => reviewFiling.mutate()}>Validate and review filing</Button>}
              <Button
                loading={submit.isPending}
                disabled={busy || !review || step !== 2 || !confirmedFiling || !acceptedFees || !registration.pricing || (!!registration.campaign_sid && !registration.campaign_retry_supported)}
                onClick={() => submit.mutate()}
              >
                Authorize fees and submit filing
              </Button>
            </Stack>

            {registration.blocked_reason && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                {registration.blocked_reason}
              </Typography>
            )}
          </>
        )}
        {!!filings?.length && <Accordion sx={{ mt: 2 }}><AccordionSummary>Authorized filing history</AccordionSummary><AccordionDetails>
          {filings.map(filing => <Typography key={filing.version} level="body-sm">Version {filing.version} · {new Date(filing.created_at).toLocaleString()} · Authorized fees: {Object.entries(filing.fees).map(([key, value]) => `${key.replaceAll('_', ' ')} $${value}`).join(', ')}</Typography>)}
        </AccordionDetails></Accordion>}
      </CardContent>
    </Card>
  );
};

const MemberReadiness: React.FC = () => {
  const { data, isError } = useQuery({ queryKey: ['messaging-readiness'], queryFn: async () => (await api.get<MessagingReadiness>('/api/inbox/a2p-registration/readiness/')).data, refetchInterval: 30000 });
  return <Alert color={data?.can_send ? 'success' : 'neutral'} sx={{ mb: 3 }}><Box>
    <Typography level="title-sm">Business texting: {data?.status_display || 'Checking status'}</Typography>
    <Typography level="body-sm">{isError ? 'Could not load texting status. Refresh to try again.' : data?.reason}</Typography>
    <Typography level="body-xs">An organization admin manages registration and number assignments.</Typography>
  </Box></Alert>;
};

export const CarrierRegistrationCard: React.FC = () => {
  const { user } = useAuth();
  if (!user?.is_superuser && !user?.is_organization_admin) return <MemberReadiness />;
  const organizationId = user?.organization?.id || '';
  return <RegistrationForm key={organizationId} organizationId={organizationId} />;
};

export default CarrierRegistrationCard;
